import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { promptRequest } from '@/hooks/prompts/lib'
import { playbookRunQueryKeys, promptsQueryKeys } from '@/hooks/prompts/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { SavedPrompt } from '@/types/library/SavedPrompt'

interface UseDeleteSavedPromptArgs {
  orgId: UUID | ''
}

interface DeleteSavedPromptRequest {
  promptId: UUID
}

const deleteSavedPrompt = async (
  promptId: UUID,
  accessToken: string | undefined | null,
): Promise<void> =>
  promptRequest<void>(
    'DELETE',
    `${API_URL}/api/v1/prompts/${promptId}`,
    accessToken,
  )

export const useDeletePrompt = ({ orgId }: UseDeleteSavedPromptArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<
    void,
    Error,
    DeleteSavedPromptRequest,
    PaginatedResponse<SavedPrompt> | undefined
  >({
    mutationFn: ({ promptId }) => deleteSavedPrompt(promptId, accessToken),
    onMutate: async ({ promptId }) => {
      const key = promptsQueryKeys.organization({ orgId })
      await queryClient.cancelQueries({ queryKey: key })

      // snapshot the previous value
      const previousData =
        queryClient.getQueryData<PaginatedResponse<SavedPrompt>>(key)

      // optimistically update
      if (previousData) {
        const updatedData = previousData.data.filter((p) => p.id !== promptId)
        queryClient.setQueryData<PaginatedResponse<SavedPrompt>>(key, {
          count: Math.max(0, previousData.count - 1),
          data: updatedData,
          has_more: previousData.has_more,
        })
      }

      // return rollback value
      return previousData
    },
    onError: (err, _req, context) => {
      const key = promptsQueryKeys.organization({ orgId })
      queryClient.setQueryData(key, context)
      handleError(err)
    },
    onSuccess: () => {
      const key = promptsQueryKeys.organization({ orgId })
      // Deleting a playbook deletes its scorecards, so the runs graded against them lose their grades.
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: key }),
        queryClient.invalidateQueries({
          queryKey: playbookRunQueryKeys.allGrades(),
        }),
        queryClient.invalidateQueries({
          queryKey: playbookRunQueryKeys.allVerdicts(),
        }),
      ])
    },
  })
}
